import {Injectable} from '@angular/core'
import {CreateBetslipDto} from '@/api/player-api/models/create-betslip-dto'
import {BetslipData} from '@/features/betslip/betslip-create/lib/models/betslip-data'
import {CreateBetslipSystemDto} from '@/api/player-api/models/create-betslip-system-dto'
import {BetslipSelection} from '@/features/betslip/betslip-create/lib/models/betslip-selection'
import {CreateBetslipSelectionDto} from '@/api/player-api/models/create-betslip-selection-dto'
import {BetslipOperations} from '@/features/betslip/betslip-create/helpers/betslip-operations'
import {BetslipConfig} from '@/features/betslip/betslip-create/lib/betslip-config'
import {BetType} from '@/api/player-api/models/bet-type'

@Injectable({providedIn: 'root'})
export class BetslipConverterService {

	public createPayload(betslipData: BetslipData): CreateBetslipDto {
		return {
			betType: betslipData.betType,
			selections: this.convertSelectionsToDto(betslipData.selections),
			stake: this.chargedStake(betslipData),
			maxOdds: betslipData.maxOdds,
			minOdds: betslipData.minOdds,
			maxPayout: betslipData.maxPayout,
			minPayout: betslipData.minPayout,
			numOfComb: betslipData.totalCombs,
			systems: this.createSystemsDto(betslipData.systems),
			live: BetslipOperations.isLiveBetslip(betslipData.selections)
		}
	}

	/**
	 * What the slip actually charges, which is not always what the player typed.
	 *
	 * Every combination of a system carries the same stake, so a stake that does not divide
	 * evenly is not reachable: 50 over 7 combinations charges 7.1428 * 7 = 49.9996. The stake
	 * the player enters stays a target on `betslipData.stake` for the UI; the request has to
	 * carry the amount that is really at risk, or the back end — which derives it as the sum
	 * of the combination stakes — will disagree with us about the size of the bet.
	 *
	 * multiSingle is the same story with the remainder distributed across selections, and it
	 * does sum back to the entered stake exactly; it is summed here for the same reason.
	 */
	private chargedStake(betslipData: BetslipData): number {
		if (BetslipOperations.isSystemBetslip(betslipData.betType)) {
			return BetslipOperations.roundValue(
				betslipData.systems.filter((s) => s.checked).reduce((acc, s) => acc + s.combinationStake, 0),
				BetslipConfig.ODDS_CALC_ROUNDING_PRECISION)
		}

		if (betslipData.betType === BetType.multiSingle) {
			return BetslipOperations.roundValue(
				betslipData.selections.reduce((acc, s) => acc + (s.stake ?? 0), 0),
				BetslipConfig.ODDS_CALC_ROUNDING_PRECISION)
		}

		return BetslipOperations.roundValue(betslipData.stake, BetslipConfig.ODDS_CALC_ROUNDING_PRECISION)
	}

	private convertSelectionsToDto(selections: BetslipSelection[]): CreateBetslipSelectionDto[] {
		return selections.map((s) => ({
			odds: s.odds,
			matchId: s.matchId,
			marketId: s.marketId,
			stake: s.stake ?? 0,
			banker: s.isBanker ?? false,
			oddsId: s.oddsId,
			matchName: s.matchName,
			marketName: s.marketLabel,
			outcomeName: s.outcomeLabel,
			live: s.isLiveMatch
		}))
	}

	private createSystemsDto(systems: BetslipData['systems']): CreateBetslipSystemDto[] {
		return systems
			.filter((bs) => bs.checked)
			.map((bs) => ({
				stake: bs.combinationStake,
				count: bs.count,
				stakePerComb: bs.stakePerComb
			}))
	}
}
